import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import toast from 'react-hot-toast';
import { AdminHero } from '@/components/admin/ui';
import { AuthedImage } from '@/components/media/AuthedImage';

interface Evidence {
  reason: string;
  notes?: string;
  buyerPhotos: string[];
  sellerResponse?: string;
  sellerPhotos: string[];
  responded: boolean;
  buyerDisputeCount: number;
  dropoffProof?: string;
  collectionProof?: string;
}
interface EscrowRow {
  id: string;
  reference: string;
  productTitle?: string;
  buyerHandle: string;
  sellerHandle: string;
  amountGBP: number;
  state: string;
  daysHeld: number;
  disputed: boolean;
  evidence?: Evidence;
}

interface PayoutRow {
  id: string;
  userId: string;
  name: string;
  email: string;
  bankLast4?: string | null;
  stripeAccountId?: string | null;
  canAutoPay: boolean;
  amountGBP: number;
  description: string;
  requestedAt: string;
  walletBalanceGBP: number;
}

const REASON_LABELS: Record<string, string> = {
  WRONG_ITEM: 'Wrong item sent', EMPTY_PARCEL: 'Empty / item missing', NOT_AS_DESCRIBED: 'Not as described',
  NOT_RECEIVED: 'Never arrived', DAMAGED: 'Arrived damaged', COUNTERFEIT: 'Suspected counterfeit',
};

export default function EscrowPayouts() {
  const qc = useQueryClient();
  const { data: rows = [] } = useQuery<EscrowRow[]>({
    queryKey: ['admin', 'escrow'],
    queryFn: async () => (await api.get('/admin/escrow')).data,
  });

  const release = useMutation({
    mutationFn: async (id: string) => (await api.post(`/admin/escrow/${id}/release`)).data,
    onSuccess: () => { toast.success('Released to seller wallet.'); qc.invalidateQueries({ queryKey: ['admin', 'escrow'] }); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not release.'),
  });
  const refund = useMutation({
    mutationFn: async (id: string) => (await api.post(`/admin/escrow/${id}/refund`)).data,
    onSuccess: () => { toast.success('Refunded to buyer.'); qc.invalidateQueries({ queryKey: ['admin', 'escrow'] }); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not refund.'),
  });

  // Adjudicated dispute rulings - these move money AND trust scores, so false
  // claims and misdescribed items both leave a permanent mark.
  const resolve = useMutation({
    mutationFn: async (p: { id: string; outcome: 'REFUND_BUYER' | 'RELEASE_SELLER' | 'PARTIAL'; refundGBP?: number }) =>
      (await api.post(`/admin/disputes/${p.id}/resolve`, { outcome: p.outcome, refundGBP: p.refundGBP })).data,
    onSuccess: (_d, p) => {
      toast.success(
        p.outcome === 'REFUND_BUYER' ? 'Dispute upheld - buyer refunded, seller trust lowered.'
        : p.outcome === 'RELEASE_SELLER' ? 'Claim rejected - seller paid, buyer trust lowered.'
        : 'Partial ruling applied.',
      );
      qc.invalidateQueries({ queryKey: ['admin', 'escrow'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not resolve dispute.'),
  });

  /* ── Member withdrawal approvals ── */
  const { data: payouts = [] } = useQuery<PayoutRow[]>({
    queryKey: ['admin', 'payouts'],
    queryFn: async () => (await api.get('/admin/payouts')).data,
  });
  const approvePayout = useMutation({
    mutationFn: async (id: string) => (await api.post(`/admin/payouts/${id}/approve`)).data,
    onSuccess: (d: any) => {
      toast.success(d?.sent ? `£${Number(d.amountGBP).toFixed(2)} sent to their bank.` : 'Payout approved.');
      qc.invalidateQueries({ queryKey: ['admin', 'payouts'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not approve the payout.'),
  });
  const rejectPayout = useMutation({
    mutationFn: async (id: string) => (await api.post(`/admin/payouts/${id}/reject`, {})).data,
    onSuccess: (d: any) => {
      toast.success(`£${Number(d?.refundedGBP ?? 0).toFixed(2)} returned to their wallet.`);
      qc.invalidateQueries({ queryKey: ['admin', 'payouts'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not reject the payout.'),
  });

  const disputes = rows.filter((r) => r.disputed);
  const held = rows.filter((r) => !r.disputed);

  return (
    <div className="p-6 md:p-10">
      <AdminHero
        eyebrow="Admin · Money & trust"
        title="Escrow & payouts"
        subtitle="Release or refund held funds, adjudicate disputes, and approve seller withdrawals."
        stats={[
          { label: 'In escrow', value: rows.length },
          { label: 'Disputed', value: rows.filter((r) => r.disputed).length, accent: rows.some((r) => r.disputed) ? 'bad' : undefined },
          { label: 'Payouts due', value: payouts.length, accent: payouts.length ? 'gold' : undefined },
        ]}
      />

      {/* ─── Withdrawals awaiting approval ─── */}
      <section className="mt-8">
        <div className="flex items-center gap-3 mb-4">
          <h2 className="display text-2xl">Withdrawals awaiting approval</h2>
          {payouts.length > 0 && (
            <span className="rounded-full bg-warn text-ink text-[11px] font-bold px-2.5 py-0.5">{payouts.length}</span>
          )}
        </div>
        {payouts.length === 0 ? (
          <div className="card p-6 text-sm text-mute">
            Nothing waiting. When a seller requests a withdrawal it appears here. Pay them in Stripe, then mark it as paid.
          </div>
        ) : (
          <div className="space-y-3">
            {payouts.map((p) => (
              <div key={p.id} className="card p-4 flex flex-wrap items-center gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-ink">{p.name}</span>
                    <span className="text-[12px] text-mute">{p.email}</span>
                    {p.canAutoPay
                      ? <span className="pill pill-ok text-[9px]">Stripe connected</span>
                      : <span className="pill bg-cream-deep text-mute text-[9px]">No Stripe account</span>}
                  </div>
                  <div className="text-[12px] text-mute mt-1">
                    Bank ••••{p.bankLast4 ?? '-'} · Stripe {p.stripeAccountId ?? 'not set'} · requested {new Date(p.requestedAt).toLocaleString()} · wallet now £{p.walletBalanceGBP.toFixed(2)}
                  </div>
                </div>
                <div className="display text-2xl text-ink shrink-0">£{p.amountGBP.toFixed(2)}</div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => rejectPayout.mutate(p.id)}
                    disabled={rejectPayout.isPending || approvePayout.isPending}
                    className="btn btn-outline btn-sm"
                  >
                    Decline &amp; refund
                  </button>
                  <button
                    onClick={() => approvePayout.mutate(p.id)}
                    disabled={approvePayout.isPending || rejectPayout.isPending}
                    className="btn btn-primary btn-sm"
                  >
                    {approvePayout.isPending ? 'Saving…' : 'Mark as paid'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
      <p className="text-sm text-mute mt-2">Review disputes with both sides' evidence, then release to the seller or refund the buyer.</p>

      {/* ─── Disputes needing a decision ─── */}
      <div className="mt-8">
        <div className="flex items-center gap-2 mb-3">
          <h2 className="display text-2xl">Disputes to resolve</h2>
          {disputes.length > 0 && <span className="pill pill-bad text-[10px]">{disputes.length}</span>}
        </div>
        {disputes.length === 0 ? (
          <div className="card p-6 text-mute text-sm">No open disputes. Nice and calm.</div>
        ) : (
          <div className="space-y-4">
            {disputes.map((r) => (
              <DisputeCard key={r.id} row={r}
                onRelease={() => resolve.mutate({ id: r.id, outcome: 'RELEASE_SELLER' })}
                onRefund={() => resolve.mutate({ id: r.id, outcome: 'REFUND_BUYER' })}
                onPartial={(gbp) => resolve.mutate({ id: r.id, outcome: 'PARTIAL', refundGBP: gbp })}
                busy={resolve.isPending} />
            ))}
          </div>
        )}
      </div>

      {/* ─── Held funds (no dispute) ─── */}
      <div className="mt-10">
        <h2 className="display text-2xl mb-3">Funds held in escrow</h2>
        <div className="card overflow-x-auto">
          {held.length ? (
            <div className="min-w-[720px] divide-y divide-warmline">
              {held.map((r) => (
                <div key={r.id} className="grid grid-cols-12 items-center gap-3 p-5 text-sm">
                  <div className="col-span-4">
                    <div className="text-xs text-mute mono">{r.reference}</div>
                    <div className="truncate">{r.productTitle ?? `${r.buyerHandle} → ${r.sellerHandle}`}</div>
                    <div className="text-[11px] text-mute">{r.buyerHandle} → {r.sellerHandle}</div>
                  </div>
                  <div className="col-span-2 display">£{r.amountGBP.toFixed(2)}</div>
                  <div className="col-span-2"><span className="pill pill-gold text-[10px]">{r.state}</span></div>
                  <div className="col-span-1 text-xs text-mute">{r.daysHeld}d</div>
                  <div className="col-span-3 flex justify-end gap-2">
                    <button onClick={() => release.mutate(r.id)} className="btn btn-gold btn-sm">Release</button>
                    <button onClick={() => refund.mutate(r.id)} className="btn btn-outline btn-sm">Refund</button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 text-mute text-sm">No undisputed funds held.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function DisputeCard({ row, onRelease, onRefund, onPartial, busy }: { row: EscrowRow; onRelease: () => void; onRefund: () => void; onPartial: (gbp: number) => void; busy: boolean }) {
  const [open, setOpen] = useState(true);
  const e = row.evidence!;
  const flagged = e.buyerDisputeCount >= 3;

  const askPartial = () => {
    const raw = window.prompt(`Partial refund to buyer (max £${row.amountGBP.toFixed(2)}); the remainder is released to the seller:`);
    if (!raw) return;
    const gbp = Number(raw);
    if (!Number.isFinite(gbp) || gbp <= 0 || gbp >= row.amountGBP) { toast.error('Enter an amount between £0 and the item price.'); return; }
    onPartial(gbp);
  };

  return (
    <div className="card p-0 overflow-hidden border-bad/30">
      <div className="flex items-center justify-between gap-3 p-5 bg-bad/5 border-b border-bad/20">
        <div className="min-w-0">
          <div className="text-xs text-mute mono">{row.reference}</div>
          <div className="display text-lg truncate">{row.productTitle}</div>
          <div className="text-[12px] text-mute">{row.buyerHandle} → {row.sellerHandle} · held {row.daysHeld}d</div>
        </div>
        <div className="text-right shrink-0">
          <div className="display text-2xl">£{row.amountGBP.toFixed(2)}</div>
          <button onClick={() => setOpen((v) => !v)} className="text-[11px] text-gold-dark hover:underline">{open ? 'Hide evidence' : 'Show evidence'}</button>
        </div>
      </div>

      {open && (
        <div className="grid md:grid-cols-2 gap-4 p-5">
          {/* Buyer side */}
          <div className="rounded-luxe border border-warmline p-4">
            <div className="flex items-center justify-between">
              <div className="stamp text-bad text-[9px]">BUYER'S REPORT</div>
              <span className={`stamp text-[9px] ${flagged ? 'text-bad' : 'text-mute'}`}>
                {e.buyerDisputeCount} disputes filed{flagged ? ' · ⚠ FLAGGED' : ''}
              </span>
            </div>
            <div className="text-sm font-medium mt-2">{REASON_LABELS[e.reason] ?? e.reason}</div>
            {e.notes && <p className="text-[13px] text-ink-700 mt-1 leading-relaxed">“{e.notes}”</p>}
            <div className="mt-3">
              <div className="stamp text-mute text-[9px] mb-1">EVIDENCE</div>
              {e.buyerPhotos.length || e.collectionProof ? (
                <div className="flex flex-wrap gap-1.5">
                  {[...e.buyerPhotos, ...(e.collectionProof ? [e.collectionProof] : [])].map((u, i) => (
                    <AuthedImage key={i} path={u} className="w-16 h-16 object-cover rounded border border-warmline" />
                  ))}
                </div>
              ) : <p className="text-[12px] text-mute">No photo provided.</p>}
            </div>
          </div>

          {/* Seller side */}
          <div className="rounded-luxe border border-warmline p-4">
            <div className="flex items-center justify-between">
              <div className="stamp text-gold-dark text-[9px]">SELLER'S DEFENCE</div>
              <span className={`stamp text-[9px] ${e.responded ? 'text-ok' : 'text-warn'}`}>{e.responded ? 'Responded' : 'No response yet'}</span>
            </div>
            {e.sellerResponse
              ? <p className="text-[13px] text-ink-700 mt-2 leading-relaxed">“{e.sellerResponse}”</p>
              : <p className="text-[12px] text-mute mt-2">The seller has not responded to this dispute.</p>}
            <div className="mt-3">
              <div className="stamp text-mute text-[9px] mb-1">PROOF OF DISPATCH &amp; COUNTER-EVIDENCE</div>
              {e.dropoffProof || e.sellerPhotos.length ? (
                <div className="flex flex-wrap gap-1.5">
                  {[...(e.dropoffProof ? [e.dropoffProof] : []), ...e.sellerPhotos].map((u, i) => (
                    <AuthedImage key={i} path={u} className="w-16 h-16 object-cover rounded border border-warmline" />
                  ))}
                </div>
              ) : <p className="text-[12px] text-mute">No dispatch proof on file.</p>}
            </div>
          </div>

          {/* Decision */}
          <div className="md:col-span-2 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 rounded-luxe bg-cream-deep/50 border border-warmline p-4">
            <p className="text-[12px] text-mute">
              Rulings move money <em>and</em> trust: upholding the claim lowers the seller's score; rejecting it lowers the buyer's. Partial splits carry no penalty.
            </p>
            <div className="flex gap-2 shrink-0">
              <button onClick={onRefund} disabled={busy} className="btn btn-sm btn-outline">↩ Uphold - refund buyer</button>
              <button onClick={askPartial} disabled={busy} className="btn btn-sm btn-outline">◐ Partial split</button>
              <button onClick={onRelease} disabled={busy} className="btn btn-sm btn-gold !text-ink">→ Reject - pay seller</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
